import { useState } from "react";
import { RotateCcw } from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import type { SellOrderRow } from "../orderForm/components/SellOrders";
import type { PortfolioReportItem } from "@/api/types";
import { Switch } from "@/components/switch";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/utils/number";

// Funds are traded at NAV, so they have no market/limit order type.
export const isFundClass = (class2code?: string) =>
  ["FI", "EF"].includes(class2code ?? "");

const holdingQuantityFormatter = new Intl.NumberFormat("no-NO", {
  maximumFractionDigits: 6,
});

type Props = {
  rows: SellOrderRow[];
  holdings: PortfolioReportItem[];
  onPatch: (securityId: number, patch: Partial<SellOrderRow>) => void;
  onReset: () => void;
  onSellAll: () => void;
};

const fieldClass =
  "rounded-md border border-border bg-background px-2 py-1 text-[11px] tabular-nums text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-40";

export function SellHoldingsTable({
  rows,
  holdings,
  onPatch,
  onReset,
  onSellAll,
}: Props) {
  const [id] = useState(() => uuidv4());
  const [hideZeroHoldings, setHideZeroHoldings] = useState(true);
  const visibleRows = hideZeroHoldings
    ? rows.filter((row) => row.holding !== 0)
    : rows;
  const showOrderColumns = visibleRows.some(
    (row) => !isFundClass(row.class2code)
  );
  const holdingById = new Map(
    holdings.map((holding) => [holding.security.id, holding])
  );
  const canSell = rows.some((row) => row.holding > 0);
  const hasOrders = rows.some(
    (row) =>
      row.sellQty ||
      row.type ||
      row.limitPrice ||
      row.duration ||
      row.feePercent
  );

  return (
    <section
      aria-label="Salgsordre"
      className="overflow-hidden rounded-xl border border-border bg-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div>
          <h3 className="text-[13px] font-semibold text-foreground">
            Beholdning
          </h3>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {visibleRows.length} av {rows.length} verdipapirer · angi antall du
            vil selge
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={onReset}
            disabled={!hasOrders}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
          >
            <RotateCcw aria-hidden="true" className="size-3" /> Nullstill salg
          </button>
          <button
            type="button"
            onClick={onSellAll}
            disabled={!canSell}
            className="rounded-md border border-border px-2 py-1 text-[11px] font-medium text-foreground transition-colors hover:border-primary/40 hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Selg alt
          </button>
          <div className="flex items-center gap-2">
            <label
              htmlFor={`${id}-hide-zero`}
              className="text-[11px] text-muted-foreground"
            >
              Skjul nuller
            </label>
            <Switch
              id={`${id}-hide-zero`}
              size="sm"
              checked={hideZeroHoldings}
              onCheckedChange={setHideZeroHoldings}
            />
          </div>
        </div>
      </div>

      {visibleRows.length === 0 ? (
        <p className="border-t border-border/60 px-4 py-5 text-[12px] text-muted-foreground">
          {rows.length === 0
            ? "Ingen beholdning å selge fra."
            : "Ingen beholdninger med antall større enn null. Slå av «Skjul nuller» for å vise alle."}
        </p>
      ) : (
        <table className="w-full table-fixed text-[12px] [&_td]:break-words [&_th]:break-words">
          <caption className="sr-only">Beholdning og salgsantall</caption>
          <colgroup>
            <col className={showOrderColumns ? "w-[26%]" : "w-[29%]"} />
            <col className={showOrderColumns ? "w-[12%]" : "w-[14%]"} />
            <col className={showOrderColumns ? "w-[11%]" : "w-[13%]"} />
            <col className={showOrderColumns ? "w-[12%]" : "w-[14%]"} />
            <col className={showOrderColumns ? "w-[15%]" : "w-[17%]"} />
            {showOrderColumns && <col className="w-[12%]" />}
            <col className={showOrderColumns ? "w-[12%]" : "w-[13%]"} />
          </colgroup>
          <thead className="border-y border-border/60 bg-muted/50 text-[9px] uppercase tracking-wide text-muted-foreground">
            <tr>
              <th scope="col" className="px-3 py-2 text-left font-semibold">
                Verdipapir
              </th>
              <th scope="col" className="px-2 py-2 text-right font-semibold">
                Beholdning
              </th>
              <th scope="col" className="px-2 py-2 text-right font-semibold">
                Siste kurs
              </th>
              <th scope="col" className="px-2 py-2 text-right font-semibold">
                Verdi (NOK)
              </th>
              <th scope="col" className="px-2 py-2 text-left font-semibold">
                Selg antall
              </th>
              {showOrderColumns && (
                <th scope="col" className="px-2 py-2 text-left font-semibold">
                  Ordretype
                </th>
              )}
              <th scope="col" className="px-3 py-2 text-right font-semibold">
                Estimert beløp
              </th>
            </tr>
          </thead>
          {visibleRows.map((row) => {
            const holding = holdingById.get(row.securityId);
            const marketUnitPrice = holding?.security?.latestMarketData?.close1;
            const quantity = parseFloat(row.sellQty.replace(",", ".")) || 0;
            const estimate = quantity * row.marketUnitPrice;
            const isFund = isFundClass(row.class2code);
            const showLimit =
              showOrderColumns && !isFund && row.type === "Limit";

            return (
              <tbody
                key={row.securityId}
                className="border-b border-border/60 last:border-b-0"
              >
                <tr
                  className={cn(
                    "transition-colors hover:bg-muted/25",
                    quantity > 0 && "bg-primary/[0.035]"
                  )}
                >
                  <th scope="row" className="px-3 py-2 text-left font-normal">
                    <div className="min-w-0">
                      <p
                        className="truncate font-medium text-foreground"
                        title={row.name}
                      >
                        {row.name}
                      </p>
                      <p className="mt-0.5 truncate text-[10px] text-muted-foreground">
                        {holding?.security.isinCode || row.securityCode}
                      </p>
                    </div>
                  </th>
                  <td className="px-2 py-2 text-right tabular-nums text-foreground">
                    {holdingQuantityFormatter.format(row.holding)}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums text-foreground">
                    {marketUnitPrice != null ? (
                      <>
                        <span className="text-[10px] text-muted-foreground">
                          {holding?.security.currency?.securityCode}{" "}
                        </span>
                        {formatNumber(marketUnitPrice, 2)}
                      </>
                    ) : (
                      "–"
                    )}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums text-foreground">
                    {holding ? formatNumber(holding.marketValue, 2) : "–"}
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex w-full max-w-[7.5rem] overflow-hidden rounded-md border border-border bg-background focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/20">
                      <input
                        type="text"
                        inputMode="decimal"
                        aria-label={`Selg antall for ${row.name}`}
                        placeholder="0"
                        value={row.sellQty}
                        disabled={row.holding <= 0}
                        onChange={(event) =>
                          onPatch(row.securityId, {
                            sellQty: event.target.value
                              .replace(/[^\d.,]/g, "")
                              .replace(",", "."),
                          })
                        }
                        className="min-w-0 flex-1 appearance-none rounded-none border-0 bg-transparent px-2 py-1.5 text-[12px] tabular-nums text-foreground shadow-none outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-40"
                      />
                      <button
                        type="button"
                        aria-label={`Selg alt av ${row.name}`}
                        disabled={row.holding <= 0}
                        onClick={() =>
                          onPatch(row.securityId, {
                            sellQty: String(row.holding),
                          })
                        }
                        className="shrink-0 whitespace-nowrap border-l border-border/50 bg-muted/40 px-1.5 text-[10px] font-medium text-primary transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Alt
                      </button>
                    </div>
                  </td>
                  {showOrderColumns && (
                    <td className="px-2 py-2">
                      {isFund ? (
                        <span
                          className="text-[11px] text-muted-foreground"
                          title="Fond handles til fondets andelsverdi (NAV)."
                        >
                          NAV
                        </span>
                      ) : (
                        <select
                          aria-label={`Ordretype for ${row.name}`}
                          value={row.type || "Market"}
                          disabled={row.holding <= 0}
                          onChange={(event) =>
                            onPatch(row.securityId, {
                              type:
                                event.target.value === "Limit"
                                  ? "Limit"
                                  : "Market",
                              ...(event.target.value === "Market"
                                ? { limitPrice: "", duration: "" }
                                : {}),
                            })
                          }
                          className={cn(fieldClass, "w-full min-w-0")}
                        >
                          <option value="Market">Marked</option>
                          <option value="Limit">Limit</option>
                        </select>
                      )}
                    </td>
                  )}
                  <td
                    className={cn(
                      "px-3 py-2 text-right font-medium tabular-nums",
                      quantity > 0 ? "text-foreground" : "text-muted-foreground"
                    )}
                  >
                    {formatNumber(estimate, 2)}
                  </td>
                </tr>
                {showLimit && (
                  <tr className="bg-primary/[0.035]">
                    <td colSpan={7} className="px-3 pb-1 pt-1">
                      <div
                        role="group"
                        aria-label={`Limitvilkår for ${row.name}`}
                        className="ml-1 flex flex-wrap items-center gap-x-4 gap-y-1 border-l-2 border-primary/40 pl-3 text-[11px] text-muted-foreground"
                      >
                        <label className="flex items-center gap-1.5">
                          Limitkurs
                          <input
                            type="text"
                            inputMode="decimal"
                            aria-label={`Limitkurs for ${row.name}`}
                            placeholder="0,00"
                            value={row.limitPrice}
                            onChange={(event) =>
                              onPatch(row.securityId, {
                                limitPrice: event.target.value,
                              })
                            }
                            className={cn(fieldClass, "w-24")}
                          />
                        </label>
                        <label className="flex items-center gap-1.5">
                          Varighet
                          <input
                            type="text"
                            inputMode="numeric"
                            aria-label={`Varighet i dager for ${row.name}`}
                            placeholder="1"
                            value={
                              /^\d+$/.test(row.duration) ? row.duration : ""
                            }
                            onChange={(event) =>
                              onPatch(row.securityId, {
                                duration: event.target.value.replace(/\D/g, ""),
                              })
                            }
                            className={cn(fieldClass, "w-14")}
                          />
                          dager
                        </label>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            );
          })}
        </table>
      )}
    </section>
  );
}
